/*
 *
 * CostsShopPickerPopup
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 02-Oct-2025
*/

/*
 * -----------------------------------------------
 * COMPONENT PURPOSE:
 * This component renders a full-screen popup to allow users
 * to filter and select points of sale (shops) grouped by tabs.
 * The selection is stored locally inside the popup until the user
 * confirms it by clicking "Applica selezione".
 *
 * MAIN FEATURES:
 * - Keeps the editable draft separate from the confirmed Redux snapshot
 * - Allows per-tab selection via ShopSelector
 * - Tracks tab switching and resets selections if needed
 * - Updates the confirmed snapshot only after a successful selection request
 * - Ensures accessibility and mobile layout responsiveness
 *
 * USED IN:
 * - Main layout (via PageTemplate or similar wrapper)
 * -----------------------------------------------
 */

import React, { useState, useEffect, useRef, useMemo, useContext } from 'react'
import PropTypes from 'prop-types'
import Toggle from 'Hoc/ToggleComponent'
import { useMobileDetect } from 'Hoc/ListenerProvider'
import Dialog from '@mui/material/Dialog'
import FlowTemplate from 'Components/Template/FlowTemplate'
import PortalButton from '@nexidigital/portalbutton'
import PortalTabs from '@nexidigital/portaltabs'
import { Dismiss } from '@nexidigital/iconlibrary'
import CostsShopSelector from 'Components/CostsShopSelector'
import GroupsInfoPopup from 'Components/GroupsInfoPopup'
import formattedMessage from './languageModule'
import { AnalyticsContext } from 'Hoc/AnalyticsProvider'
import { mergeSelectionWithParents } from 'Utility/shopListNormalizer'
import './CostsShopPickerPopup.scss'

const CostsShopPickerPopup = (props) => {
  const { isSmallScreen } = useMobileDetect()
  const { setPopupClickAnalytics } = useContext(AnalyticsContext)

  // Draft selections; the confirmed snapshot changes only after a successful POST.
  const [localSelections, setLocalSelections] = useState({})

  // Prevent UI from rendering until initial local state is ready.
  const [isInitialized, setIsInitialized] = useState(false)
  // Tracks currently active tab.
  const [selectedTab, setSelectedTab] = useState(() => {
    // Find the index of the first group with selected: true

    if (props.localSelectedTab !== null) {
      return props.localSelectedTab
    }

    const foundIndex = props.selectorGroups.findIndex(group => group.selected === true)
    // If localSelectedTab is null and no group is selected, default to 0
    return foundIndex !== -1 ? foundIndex : 0
  })

  // Needed to compare old tab vs new tab when switching.
  const previousTabRef = useRef(0)

  // This effect runs when the popup opens.
  const [isGroupInfoDialogOpen, setIsGroupInfoDialogOpen] = useState(false)

  const [dirtyTabs, setDirtyTabs] = useState({})

  const prevIndex = previousTabRef.current
  const tabSelected = props.selectorGroups[prevIndex]?.group

  const groupLabelMapper = (group, groupDisplay) => {
    const map = {
      OTHER: formattedMessage.onlyPosTerminals
      // add more mappings as needed
    }

    return map[group] || groupDisplay || group.replace(/_/g, ' ')
  }

  const labelToGroupMapper = useMemo(() => {
    const reverseMap = {
      [formattedMessage.onlyPosTerminals]: 'OTHER'
      // aggiungi altri se necessario
    }

    props.selectorGroups.forEach(({ group, groupDisplay }) => {
      const label = groupLabelMapper(group, groupDisplay)
      if (!reverseMap[label]) reverseMap[label] = group
    })

    return reverseMap
  }, [props.selectorGroups])

  // Costruisci la struttura mergedGroups
  const mergedGroups = useMemo(() => props.selectorGroups.map(group => ({
    ...group,
    shopsList: mergeSelectionWithParents(
      group.shopsList,
      localSelections[group.group] || []
    )
  })), [props.selectorGroups, localSelections])

  useEffect(
    () => () => {
      props.actions.doSetLocalSelectedTab(null)
    },
    []
  )

  useEffect(() => {
    setPopupClickAnalytics && setPopupClickAnalytics(
      formattedMessage.shopPickerSelector,
      formattedMessage.chooseShopLabel,
      formattedMessage.applySelection,
      null,
      formattedMessage.closeLabel,
      false
    )

    // Reset del flag di refresh quando si apre il popup per evitare refresh indesiderati
    // if (props.actions?.doSetRefreshCostsContext) {
    //   props.actions.doSetRefreshCostsContext({ shouldRefresh: false, activeTab: null })
    // }
  }, [])

  useEffect(() => {
    if (props.selectorGroups && props.selectorGroups.length > 0) {
      let selectedIndex

      if (props.localSelectedTab !== null) {
        selectedIndex = props.localSelectedTab
      } else {
        const foundIndex = props.selectorGroups.findIndex(group => group.selected === true)
        selectedIndex = foundIndex !== -1 ? foundIndex : 0
      }

      if (selectedIndex !== -1) {
        setSelectedTab(selectedIndex)
        previousTabRef.current = selectedIndex // Also update the ref to avoid unwanted tab resets
      } else {
        setSelectedTab(0) // Default fallback
      }
    }
  }, [props.selectorGroups])

  /**
   * This effect tracks when the user changes tab.
   * If they switch away from a tab where selections were made but not saved,
   * and nothing was previously applied, it will clear that tab's selections.
   */
  useEffect(() => {
    const prevIndex = previousTabRef.current
    const nextIndex = selectedTab

    previousTabRef.current = nextIndex // 🔁 PRIMA DI TUTTO!

    if (prevIndex !== nextIndex) {
      const prevGroup = props.selectorGroups[prevIndex]?.group

      if (dirtyTabs[prevGroup]) {
        const hasValidSelections = localSelections?.[prevGroup]?.some(
          entry => entry.parent?.isSelected || (entry.shops && entry.shops.some(s => s.isSelected))
        )

        if (!hasValidSelections) {
          setLocalSelections(prev => ({
            ...prev,
            [prevGroup]: [] // solo se è davvero inutile
          }))
        }

        setDirtyTabs(prev => ({
          ...prev,
          [prevGroup]: false
        }))
      }
    }
  }, [selectedTab, props.selectorGroups, dirtyTabs, localSelections])

  const restoreConfirmedSelection = () => {
    const confirmedSelection = JSON.parse(JSON.stringify(props.confirmedShopSelection))
    setLocalSelections(confirmedSelection)
    props.actions.doClearCostsShopLocalSelection()
    Object.entries(confirmedSelection).forEach(([group, data]) => {
      props.actions.doSetCostsShopLocalSelection({ group, data })
    })
    setDirtyTabs({})
  }

  // Start each picker session from the last successfully applied selection.
  useEffect(() => {
    if (!props.open) {
      setIsInitialized(false)
      return
    }

    restoreConfirmedSelection()
    const confirmedIndex = props.selectorGroups.findIndex(group => props.confirmedShopSelection[group.group]?.length > 0)
    const defaultIndex = props.selectorGroups.findIndex(group => group.selected === true)
    const initialTab = confirmedIndex >= 0 ? confirmedIndex : Math.max(defaultIndex, 0)
    setSelectedTab(initialTab)
    previousTabRef.current = initialTab
    props.actions.doSetLocalSelectedTab(initialTab)
    setIsInitialized(true)
  }, [props.open, props.confirmedShopSelection])

  // Listens to the global tab change event triggered by PortalTabs
  // and maps it to internal state to track active tab index.
  useEffect(() => {
    const handleTabChange = (event) => {
      const label = event.detail?.label
      const group = labelToGroupMapper[label]
      // group ora è già nel formato originale (INTESA_SANPAOLO, NEXI_DIRECT, etc.)
      // Non serve più conversione!

      if (!group) {
        return
      }

      const index = props.selectorGroups.findIndex(t => t.group === group)
      props.actions.doSetLocalSelectedTab(index)
      // Blocca se il tab è già quello selezionato
      if (index === selectedTab) {
        return
      }
      if (index !== -1) {
        const prevGroup = props.selectorGroups[previousTabRef.current]?.group

        if (prevGroup && prevGroup !== group) {
          // Reset delle selezioni temporanee non applicate del gruppo precedente
          setLocalSelections(prev => ({
            ...prev,
            [prevGroup]: []
          }))
          props.actions.doClearCostsShopLocalSelection({ group: prevGroup })
          // Reset search filter when switching tabs - use costs-specific action
          props.actions.clearSearchTerm()
          props.actions.doGetCostsShopList({ filter: '', currentGroup: group })
        }

        previousTabRef.current = index
        setSelectedTab(index)
      }
    }

    window.addEventListener('tabSwitching', handleTabChange)
    return () => {
      window.removeEventListener('tabSwitching', handleTabChange)
    }
  }, [props.selectorGroups])

  /**
   * Triggered by the ShopSelector component every time the user toggles checkboxes.
   * It updates the local temporary state for a specific tab/group.
   */
  const handleOnChange = ({ group, updated }) => {
    const enriched = updated.map(entry => ({
      parent: {
        ...entry.parent,
        isSelected: entry.parent?.isSelected === true // forza boolean
      },
      shops: (entry.shops || []).map(shop => ({
        ...shop,
        isSelected: shop?.isSelected === true // forza boolean
      }))
    }))

    // Controlla se ci sono selezioni effettive in questo gruppo
    const hasSelections = enriched.some(entry =>
      entry.parent?.isSelected || entry.shops?.some(s => s.isSelected)
    )

    // Se ci sono selezioni in questo gruppo, pulisci gli altri gruppi
    if (hasSelections) {
      clearOtherGroups(group)
    }

    setLocalSelections(prev => ({
      ...prev,
      [group]: enriched
    }))

    setDirtyTabs(prev => ({
      ...prev,
      [group]: true
    }))
  }

  /**
   * When the user clicks "Applica selezione":
   * - Filters only the selected shops
   * - Normalizes the structure for Redux
   * - Closes the popup
   */

  const handleApply = () => {
    // Usa SOLO le selezioni del gruppo attivo (comportamento esclusivo)
    const activeGroup = props.selectorGroups[selectedTab]?.group

    // Prendi solo le selezioni del gruppo attivo
    const activeGroupSelections = props.shopLocalSelection[activeGroup] || []

    const filteredSelections = activeGroupSelections
      .filter(parent =>
        // Se ha figli, almeno uno selezionato
        (parent.shops && parent.shops.some(shop => shop.isSelected)) ||
        // Se NON ha figli, il parent è selezionato
        ((!parent.shops || parent.shops.length === 0) && parent.parent?.isSelected)
      )
      .map(parent => ({
        parent: parent.parent,
        shops: (parent.shops || []).filter(shop => shop.isSelected),
        allShopCount: parent.shops?.length ?? 0
      }))

    // Invia solo le selezioni del gruppo attivo usando l'azione specifica per costs
    props.actions.setCostsShopSelection({ group: activeGroup, data: filteredSelections, selectorGroups: props.selectorGroups })

    // Pulisci lo stato locale e chiudi il popup
    props.selectorGroups.forEach(groupObj => {
      props.actions.doClearCostsShopLocalSelection({ group: groupObj.group })
    })

    setPopupClickAnalytics && setPopupClickAnalytics(
      formattedMessage.shopPickerSelector,
      formattedMessage.chooseShopLabel,
      formattedMessage.applySelection,
      null,
      formattedMessage.closeLabel,
      formattedMessage.applySelection
    )
    props.actions.doClosePopup?.('COSTS_SHOP_PICKER')
  }

  const handleClose = () => {
    restoreConfirmedSelection()
    props.actions.clearSearchTerm()

    props.actions.doClosePopup?.('COSTS_SHOP_PICKER')
    setPopupClickAnalytics && setPopupClickAnalytics(
      formattedMessage.shopPickerSelector,
      formattedMessage.chooseShopLabel,
      formattedMessage.applySelection,
      null,
      formattedMessage.closeLabel,
      formattedMessage.closeLabel
    )
  }

  /**
   * Used to disable the "Applica selezione" button unless at least one checkbox is checked.
   */
  const normalizeSelections = (selections) => Object.fromEntries(
    Object.entries(selections).map(([group, entries]) => [
      group,
      entries
        .filter(entry => entry.shops?.some(c => c.isSelected) || entry.parent?.isSelected)
        .map(entry => ({
          parentCode: entry.parent?.code,
          parentSelected: entry.parent?.isSelected,
          selectedChildren: (entry.shops || []).filter(c => c.isSelected).map(c => c.code).sort()
        }))
    ])
  )

  const clearOtherGroups = (activeGroup) => {
    const allGroups = props.selectorGroups.map(g => g.group)
    const groupsToClear = allGroups.filter(g => g !== activeGroup)

    setLocalSelections(prev => {
      const cleared = { ...prev }
      groupsToClear.forEach(group => {
        // Pulisci lo stato Redux per i costi usando azioni specifiche per costs
        props.actions.clearCostsShopSelection(group, [])
        props.actions.doClearCostsShopLocalSelection({ group })
        cleared[group] = []
      })
      return cleared
    })
  }

  const canApply = useMemo(() => {
    const normalizedCurrent = normalizeSelections(localSelections)

    const hasAnySelection = Object.values(normalizedCurrent).some(group =>
      group.some(entry =>
        entry.parentSelected || entry.selectedChildren.length > 0
      )
    )

    // For costs context, enable button if there's any selection
    // (since it's a new selection system, we don't need to compare with original)
    return hasAnySelection
  }, [localSelections])

  // Avoid rendering while initial state is being set.
  if (!isInitialized) return null

  /**
   * Create tab content for each group (e.g. NEXI, INTESA, etc.)
   * Each tab renders a ShopSelector with its own data slice.
   */
  const tabData = props.selectorGroups.map(({ group, groupDisplay, shopsList }) => {
    const label = groupLabelMapper(group, groupDisplay)
    const selections = localSelections[group] || []
    // Prendi la lista mergeata per questo gruppo
    const mergedGroup = mergedGroups.find(g => g.group === group)
    const mergedParents = mergedGroup?.shopsList || []

    const content = (
      <div className='shopPickerShopSelection'>
        <section className='shopSelectorRow'>
          <div className='shopSelectorBox'>
            <CostsShopSelector
              key={`${group}-${props.open}-${mergedParents.length}`}
              data={{ group, initialParents: mergedParents, selections }}
              onChange={handleOnChange}
              tabSelected={tabSelected}
              clearAction={clearOtherGroups}
              shopListLoading={props.shopListLoading}
              actions={props.actions}
              shopSearchTerm={props.shopSearchTerm}
              shopLocalSelection={props.shopLocalSelection}
            />
          </div>
        </section>
      </div>
    )

    return { label, content }
  })

  return (
    <>
      <Toggle>
        <Dialog fullScreen open={props.open} onClose={handleClose} className='shopPickerDialog'>
          <FlowTemplate
            flowBackground
            titleNavigation={formattedMessage.shopPickerSelector}
            iconCloseRight={<Dismiss size={isSmallScreen ? '24' : '20'} type='line' />}
            labelButtonRightNav={formattedMessage.closeLabel}
            onClickClose={handleClose}
            emptyPage
          >
            <section className='shopPickerPopup'>
              <div className='shopPickerContainer'>
                <div className='shopPickerPopupHeader'>
                  <h1 className='shopPickerPopupTitle'>
                    {formattedMessage.chooseShopLabel}
                    {props.selectorGroups.length > 1 && (
                      <>
                        <PortalButton
                          textButton
                          size='small'
                          label={formattedMessage.whatGroupsAreInfo}
                          onClick={() => setIsGroupInfoDialogOpen(true)}
                        />
                        {isGroupInfoDialogOpen && (
                          <GroupsInfoPopup onClose={() => setIsGroupInfoDialogOpen(false)} availableGroups={props.selectorGroups.map(group => group.group)} />
                        )}
                      </>
                    )}
                  </h1>
                  <div className={`shopPickerPopupContent ${tabData.length > 1 ? 'hasTabs' : ''}`}>
                    {tabData.length > 1
                      ? (
                        <PortalTabs merchant tabData={tabData} preSelectedTab={selectedTab} />
                        )
                      : (
                          tabData[0]?.content
                        )}
                  </div>
                </div>
              </div>
              <div className='shopPickerPopupFooter'>
                <PortalButton label={formattedMessage.applySelection} onClick={handleApply} disabled={!canApply} />
              </div>
            </section>
          </FlowTemplate>
        </Dialog>
      </Toggle>
    </>
  )
}

CostsShopPickerPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  selectorGroups: PropTypes.array.isRequired,
  shopSelections: PropTypes.object.isRequired,
  actions: PropTypes.object,
  shopListLoading: PropTypes.bool,
  shopSelectionFromPage: PropTypes.string,
  shopLocalSelection: PropTypes.object,
  confirmedShopSelection: PropTypes.object,
  shopSearchTerm: PropTypes.string,
  localSelectedTab: PropTypes.number
}

CostsShopPickerPopup.defaultProps = {
  // Add here some default propTypes values
  actions: {},
  shopListLoading: false,
  shopSelectionFromPage: 'default', // Default to 'default' if not specified,
  shopLocalSelection: {},
  confirmedShopSelection: {},
  shopSearchTerm: '',
  localSelectedTab: null
}

export default CostsShopPickerPopup
